import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { REQUEST_TYPE_CONFIG } from "@/components/controller/approvals/ApprovalRequestTypeConfig";

interface Designation {
  id: string;
  action_type: string;
  min_amount: number | null;
  max_amount: number | null;
  approver_role: string | null;
  approver_user_id: string | null;
  approval_order: number;
  require_all: boolean;
  is_active: boolean;
  // resolved
  approver_name?: string;
}

const ACTION_TYPE_OPTIONS = [
  { value: "bill_approve", label: "Bill Approval" },
  { value: "invoice_approve", label: "Bill Approval (legacy)" },
  { value: "draw_approve", label: "Draw Approval" },
  { value: "rate_change", label: "Rate Change" },
  { value: "low_margin", label: "Low Margin" },
  { value: "change_order", label: "Change Order" },
  { value: "retainage_release", label: "Retainage Release" },
  { value: "void_record", label: "Void Record" },
  { value: "reopen_wo", label: "Reopen WO" },
  { value: "backcharge", label: "Backcharge" },
  { value: "vendor_approve", label: "Vendor Approval" },
  { value: "other", label: "Other" },
];

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const empty = (): Omit<Designation, "id"> => ({
  action_type: "bill_approve",
  min_amount: null,
  max_amount: null,
  approver_role: "controller",
  approver_user_id: null,
  approval_order: 1,
  require_all: false,
  is_active: true,
});

export const ApprovalRulesTab = () => {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Designation | null>(null);
  const [form, setForm] = useState<Omit<Designation, "id">>(empty());

  // Fetch designations
  const { data: designations = [], isLoading, error } = useQuery({
    queryKey: ["approval_designations"],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("approval_designations" as any) as any)
        .select("*")
        .order("action_type")
        .order("approval_order");
      if (error) throw error;

      const items = (data ?? []) as Designation[];
      const userIds = items.map((d) => d.approver_user_id).filter(Boolean) as string[];
      if (userIds.length === 0) return items;

      const { data: profiles } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name")
        .in("user_id", userIds);
      const pMap: Record<string, string> = {};
      (profiles ?? []).forEach((p: any) => {
        pMap[p.user_id] = p.display_name ?? `${p.first_name} ${p.last_name}`;
      });

      return items.map((d) => ({
        ...d,
        approver_name: d.approver_user_id ? pMap[d.approver_user_id] : undefined,
      }));
    },
  });

  // Fetch users for approver picker
  const { data: users = [] } = useQuery({
    queryKey: ["approval_designations_users"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name, role:roles(name)")
        .eq("status", "active");
      return (data ?? []).map((u: any) => ({
        userId: u.user_id,
        name: u.display_name ?? `${u.first_name} ${u.last_name}`,
        roleName: u.role?.name ?? "",
      }));
    },
  });

  const save = useMutation({
    mutationFn: async (payload: typeof form & { id?: string }) => {
      const { id, ...rest } = payload;
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (id) {
        const { error } = await (supabase
          .from("approval_designations" as any) as any)
          .update(rest)
          .eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await (supabase
          .from("approval_designations" as any) as any)
          .insert({ ...rest, company_id: companyId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approval_designations"] });
      setDialogOpen(false);
      toast.success(editing ? "Rule updated" : "Rule added");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from("approval_designations" as any) as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approval_designations"] });
      toast.success("Rule deleted");
    },
    onError: (err: any) => toast.error(err.message),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase
        .from("approval_designations" as any) as any)
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval_designations"] }),
    onError: (err: any) => toast.error(err.message),
  });

  const openNew = () => {
    setEditing(null);
    setForm(empty());
    setDialogOpen(true);
  };

  const openEdit = (d: Designation) => {
    setEditing(d);
    setForm({
      action_type: d.action_type,
      min_amount: d.min_amount,
      max_amount: d.max_amount,
      approver_role: d.approver_role,
      approver_user_id: d.approver_user_id,
      approval_order: d.approval_order,
      require_all: d.require_all,
      is_active: d.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (form.min_amount !== null && form.max_amount !== null && form.min_amount > form.max_amount) {
      toast.error("Minimum amount cannot be greater than maximum amount");
      return;
    }
    save.mutate(editing ? { ...form, id: editing.id } : form);
  };

  const getActionLabel = (type: string) =>
    ACTION_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

  const getApproverDisplay = (d: Designation) => {
    if (d.approver_user_id && d.approver_name) return d.approver_name;
    if (d.approver_role) return <span className="capitalize">{d.approver_role.replace("_", " ")}</span>;
    return "—";
  };

  const getAmountRange = (d: Designation) => {
    if (d.min_amount !== null && d.max_amount !== null) return `${fmt(d.min_amount)} – ${fmt(d.max_amount)}`;
    if (d.min_amount !== null) return `> ${fmt(d.min_amount)}`;
    if (d.max_amount !== null) return `≤ ${fmt(d.max_amount)}`;
    return "Any amount";
  };

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  if (error) return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">
          Approval rules table not yet available. Run migration <code>20260306220000_approval_designation_engine.sql</code> in the Supabase SQL Editor.
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Approval Rules</h2>
          <p className="text-sm text-muted-foreground">
            Configure who approves each request type and at what dollar thresholds.
          </p>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" /> Add Rule
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action Type</TableHead>
                <TableHead>Amount Range</TableHead>
                <TableHead>Approver</TableHead>
                <TableHead className="text-center">Step</TableHead>
                <TableHead className="text-center">Mode</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {designations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No approval rules configured. Click "Add Rule" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                designations.map((d) => (
                  <TableRow key={d.id} className={!d.is_active ? "opacity-50" : ""}>
                    <TableCell className="font-medium">
                      <Badge variant="outline" className="text-xs">
                        {getActionLabel(d.action_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{getAmountRange(d)}</TableCell>
                    <TableCell className="text-sm">{getApproverDisplay(d)}</TableCell>
                    <TableCell className="text-center text-sm">{d.approval_order}</TableCell>
                    <TableCell className="text-center text-xs">
                      {d.require_all ? "All approve" : "Any approves"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={d.is_active}
                        onCheckedChange={(checked) => toggleActive.mutate({ id: d.id, is_active: checked })}
                        disabled={toggleActive.isPending}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(d)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del.mutate(d.id)} disabled={del.isPending}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Approval Rule" : "Add Approval Rule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Request Type *</Label>
              <Select value={form.action_type} onValueChange={(v) => setForm((f) => ({ ...f, action_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min Amount ($)</Label>
                <Input
                  type="number"
                  placeholder="No minimum"
                  value={form.min_amount ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, min_amount: e.target.value ? Number(e.target.value) : null }))}
                />
              </div>
              <div>
                <Label>Max Amount ($)</Label>
                <Input
                  type="number"
                  placeholder="No maximum"
                  value={form.max_amount ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, max_amount: e.target.value ? Number(e.target.value) : null }))}
                />
              </div>
            </div>

            <div>
              <Label>Approver Role</Label>
              <Select
                value={form.approver_role ?? ""}
                onValueChange={(v) => setForm((f) => ({ ...f, approver_role: v || null, approver_user_id: null }))}
              >
                <SelectTrigger><SelectValue placeholder="Select role…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">System Administrator</SelectItem>
                  <SelectItem value="controller">Controller</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="accountant">Accounting Specialist</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Or pick a specific user below (overrides role)</p>
            </div>

            <div>
              <Label>Specific Approver (optional)</Label>
              <Select
                value={form.approver_user_id ?? ""}
                onValueChange={(v) => setForm((f) => ({ ...f, approver_user_id: v || null }))}
              >
                <SelectTrigger><SelectValue placeholder="Any user with above role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">— Use role (any matching user) —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.userId} value={u.userId}>
                      {u.name} <span className="text-muted-foreground">({u.roleName})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Step Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.approval_order}
                  onChange={(e) => setForm((f) => ({ ...f, approval_order: Number(e.target.value) || 1 }))}
                />
                <p className="text-xs text-muted-foreground mt-1">1 = first approver</p>
              </div>
              <div className="space-y-2">
                <Label>Approval Mode</Label>
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={form.require_all}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, require_all: v }))}
                  />
                  <span className="text-sm">{form.require_all ? "All must approve" : "Any one approves"}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              />
              <Label>Rule is active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editing ? "Update" : "Add Rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
